
import { Vue, Component, Watch } from 'vue-property-decorator';
import moment from 'moment';
import { BIconArrowRepeat } from 'bootstrap-vue';
import Api from '@gid/vue-common/api';
import DatePicker from '@gid/vue-common/components/DatePicker.vue';
import RevenueFunnelChart, {
  FunnelPoint,
  FunnelSegment,
  FunnelStage,
  STAGE_COLORS,
  formatKEur,
} from '../../components/accounting/RevenueFunnelChart.vue';

type TimeWindow = 'year' | 'quarter' | 'month' | 'week';
type Source = 'gid' | 's4c';
// What a stage bar is split into and what the breakdown table lists.
type Breakdown = 'source' | 'team' | 'account';

// Segments per stage bar in the team/account breakdowns. More than this and
// the tints stop being distinguishable; the tail is folded into "other".
const MAX_CHART_SEGMENTS = 8;
const OTHER_SEGMENT = '__other__';

interface FunnelRow {
  period: string;
  stage: string;
  source: Source;
  team: string;
  commercial_account: string;
  volume: number;
  count: number;
}

interface SyncState {
  entity: string;
  last_run_at: string | null;
  ok: boolean;
  rows: number;
  error: string | null;
}

interface Option {
  value: string;
  text: string;
}

const pct = new Intl.NumberFormat('de-DE', {
  style: 'percent',
  maximumFractionDigits: 0,
});

// The sync runs nightly; anything older than a missed run and a half is stale.
const STALE_AFTER_HOURS = 36;

// The API's own team buckets come back as keys and are labelled here (the
// three GiD partner buckets, and S4C's subcontracting-for-GiD bucket); Dynamics
// teams are Dynamics' own labels and pass through untouched.
const TEAM_KEYS = [
  'gid_s4c',
  'gid_external',
  'gid_unassigned',
  's4c_gid_subcontract',
];

// `start` snaps a date to the period (ISO weeks start on Monday, matching
// Postgres' date_trunc('week')); `step` advances by one period.
const MOMENT_UNIT: Record<
  TimeWindow,
  {
    start: moment.unitOfTime.StartOf;
    step: moment.unitOfTime.DurationConstructor;
  }
> = {
  year: { start: 'year', step: 'year' },
  quarter: { start: 'quarter', step: 'quarter' },
  month: { start: 'month', step: 'month' },
  week: { start: 'isoWeek', step: 'week' },
};

/** One line of the period × group table; stage keys hold the volume. */
interface BreakdownRow {
  period: string;
  label: string;
  sources: Source[];
  first?: boolean;
  subtotal?: boolean;
  [stage: string]: any;
}

const PERIOD_FORMAT: Record<TimeWindow, string> = {
  year: 'YYYY',
  quarter: '[Q]Q YYYY',
  month: 'MMM YYYY',
  week: '[KW] WW GGGG',
};

@Component({
  components: { RevenueFunnelChart, BIconArrowRepeat, DatePicker },
})
export default class RevenueFunnel extends Vue {
  timeWindow: TimeWindow = 'month';
  // The funnel is read against the current business year, so it opens at
  // 2026-01-01 (the AccountsAging next door pins its start the same way).
  dateStart: string = moment('2026-01-01').startOf('day').toISOString(true);
  dateEnd: string = moment().endOf('month').toISOString(true);

  selectedSources: Source[] = ['gid', 's4c'];
  selectedTeams: Option[] = [];
  selectedAccounts: Option[] = [];
  breakdown: Breakdown = 'source';
  showMethod = false;

  inProgress = false;
  errorMessage = '';

  rows: FunnelRow[] = [];
  stages: FunnelStage[] = [];
  sync: SyncState[] = [];
  undated: Record<string, number> = {};
  brandAliases: { brand: string; commercial_account: string }[] = [];
  // The range the current rows were fetched for; the period axis is built from
  // it rather than from the pickers so a half-typed date cannot blank the chart.
  fetchedStart = '';
  fetchedEnd = '';

  created() {
    this.fetch();
  }

  @Watch('timeWindow')
  @Watch('dateStart')
  @Watch('dateEnd')
  onRangeChange() {
    this.fetch();
  }

  // --- options --------------------------------------------------------------

  get timeWindowOptions() {
    return (['year', 'quarter', 'month', 'week'] as TimeWindow[]).map((w) => ({
      value: w,
      text: this.$t(`accounting.revenue-funnel.window.${w}`),
    }));
  }

  get sourceOptions() {
    return [
      { value: 'gid', text: this.sourceLabel('gid') },
      { value: 's4c', text: this.sourceLabel('s4c') },
    ];
  }

  get breakdownOptions() {
    return [
      { value: 'source', text: this.$t('accounting.revenue-funnel.by-source') },
      { value: 'team', text: this.$t('accounting.revenue-funnel.by-team') },
      {
        value: 'account',
        text: this.$t('accounting.revenue-funnel.by-account'),
      },
    ];
  }

  get breakdownTitle(): string {
    return this.$t(`accounting.revenue-funnel.by-${this.breakdown}`) as string;
  }

  // Teams offered are the ones present in the selected sources, GiD buckets
  // first, then Dynamics teams alphabetically.
  get teamOptions(): Option[] {
    const keys = new Set(
      this.rows
        .filter((r) => this.selectedSources.includes(r.source))
        .map((r) => r.team),
    );
    const buckets = TEAM_KEYS.filter((k) => keys.has(k));
    const dynamics = [...keys]
      .filter((k) => !TEAM_KEYS.includes(k))
      .sort((a, b) => a.localeCompare(b));
    return [...buckets, ...dynamics].map((k) => ({
      value: k,
      text: this.teamLabel(k),
    }));
  }

  // Accounts offered are those with any volume in the selected sources and
  // teams, largest first, so the top of the list is what one looks for.
  get accountOptions(): Option[] {
    const teams = new Set(this.selectedTeams.map((t) => t.value));
    const volume = new Map<string, number>();
    for (const r of this.rows) {
      if (!this.selectedSources.includes(r.source)) continue;
      if (teams.size && !teams.has(r.team)) continue;
      volume.set(
        r.commercial_account,
        (volume.get(r.commercial_account) || 0) + r.volume,
      );
    }
    return [...volume.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([k]) => ({ value: k, text: k }));
  }

  get localizedStages(): FunnelStage[] {
    return this.stages.map((s) => ({
      key: s.key,
      label: this.stageLabel(s.key),
    }));
  }

  // The key a row contributes to under the current breakdown.
  segmentOf(r: FunnelRow): string {
    if (this.breakdown === 'team') return r.team;
    if (this.breakdown === 'account') return r.commercial_account;
    return r.source;
  }

  segmentLabel(key: string): string {
    if (key === OTHER_SEGMENT) {
      return this.$t('accounting.revenue-funnel.other') as string;
    }
    if (this.breakdown === 'team') return this.teamLabel(key);
    if (this.breakdown === 'account') return key;
    return this.sourceLabel(key as Source);
  }

  // Segments ordered by their "created" volume, largest first, so the solid
  // segment is the one that matters most; a long tail folds into "other".
  get chartSegments(): FunnelSegment[] {
    if (this.breakdown === 'source') {
      return (['gid', 's4c'] as Source[])
        .filter((s) => this.selectedSources.includes(s))
        .map((s) => ({ key: s, label: this.sourceLabel(s) }));
    }
    const volume = new Map<string, number>();
    for (const r of this.filteredRows) {
      const key = this.segmentOf(r);
      volume.set(key, (volume.get(key) || 0) + r.volume);
    }
    const ranked = [...volume.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([k]) => k);
    const shown =
      ranked.length > MAX_CHART_SEGMENTS
        ? [...ranked.slice(0, MAX_CHART_SEGMENTS - 1), OTHER_SEGMENT]
        : ranked;
    return shown.map((k) => ({ key: k, label: this.segmentLabel(k) }));
  }

  segmentOpacity(index: number): number {
    const n = this.chartSegments.length;
    return n <= 1 ? 1 : 1 - (index * 0.8) / (n - 1);
  }

  get showS4c(): boolean {
    return this.selectedSources.includes('s4c');
  }

  // --- filtering --------------------------------------------------------------

  get filteredRows(): FunnelRow[] {
    const teams = new Set(this.selectedTeams.map((t) => t.value));
    const accounts = new Set(this.selectedAccounts.map((a) => a.value));
    return this.rows.filter(
      (r) =>
        this.selectedSources.includes(r.source) &&
        (!teams.size || teams.has(r.team)) &&
        (!accounts.size || accounts.has(r.commercial_account)),
    );
  }

  get hasData(): boolean {
    return this.filteredRows.some((r) => r.volume !== 0 || r.count !== 0);
  }

  // Every period in the fetched range, empty ones included: a month in which
  // nothing was invoiced is a fact the chart has to show, not skip.
  get periodKeys(): string[] {
    if (!this.fetchedStart || !this.fetchedEnd) return [];
    const { start, step } = MOMENT_UNIT[this.timeWindow];
    const end = moment(this.fetchedEnd);
    const out: string[] = [];
    for (
      let m = moment(this.fetchedStart).startOf(start);
      !m.isAfter(end) && out.length < 400;
      m = m.clone().add(1, step)
    ) {
      out.push(m.format('YYYY-MM-DD'));
    }
    return out;
  }

  get points(): FunnelPoint[] {
    const byPeriod = new Map<string, FunnelPoint>();
    for (const key of this.periodKeys) {
      byPeriod.set(key, {
        period: key,
        label: this.periodLabel(key),
        volume: {},
        count: {},
      });
    }
    const segments = new Set(this.chartSegments.map((s) => s.key));
    for (const r of this.filteredRows) {
      const p = byPeriod.get(r.period);
      if (!p) continue;
      let key = this.segmentOf(r);
      if (!segments.has(key)) key = OTHER_SEGMENT;
      const v = (p.volume[r.stage] = p.volume[r.stage] || {});
      const c = (p.count[r.stage] = p.count[r.stage] || {});
      v[key] = (v[key] || 0) + r.volume;
      c[key] = (c[key] || 0) + r.count;
    }
    return [...byPeriod.values()];
  }

  get totals(): Record<string, { volume: number; count: number }> {
    const acc: Record<string, { volume: number; count: number }> = {};
    for (const s of this.stages) acc[s.key] = { volume: 0, count: 0 };
    for (const r of this.filteredRows) {
      if (!acc[r.stage]) acc[r.stage] = { volume: 0, count: 0 };
      acc[r.stage].volume += r.volume;
      acc[r.stage].count += r.count;
    }
    return acc;
  }

  // What each stage is compared against for the "% of previous stage" figure.
  // Acceptance is two stages side by side: quotes convert out of "sent",
  // direct orders have nothing before them, and delivery follows both.
  static readonly RATIO_BASE: Record<string, string[]> = {
    sent: ['created'],
    accepted_quote: ['sent'],
    accepted_direct: [],
    delivered: ['accepted_quote', 'accepted_direct'],
    invoiced: ['delivered'],
  };

  get kpis() {
    return this.stages.map((s, i) => {
      const baseKeys =
        RevenueFunnel.RATIO_BASE[s.key] ?? (i ? [this.stages[i - 1].key] : []);
      const base = baseKeys.reduce(
        (sum, k) => sum + (this.totals[k]?.volume || 0),
        0,
      );
      const cur = this.totals[s.key];
      return {
        key: s.key,
        label: this.stageLabel(s.key),
        color: STAGE_COLORS[s.key],
        volume: cur.volume,
        count: cur.count,
        ratio: baseKeys.length && base ? cur.volume / base : null,
      };
    });
  }

  // --- tables -----------------------------------------------------------------

  get periodFields() {
    return [
      { key: 'label', label: this.$t('accounting.revenue-funnel.period') },
      ...this.stages.map((s) => ({
        key: s.key,
        label: this.stageLabel(s.key),
        class: 'text-right',
      })),
    ];
  }

  get periodRows() {
    return this.points.map((p) => {
      const row: Record<string, any> = { label: p.label };
      for (const s of this.stages) {
        const v = p.volume[s.key] || {};
        const c = p.count[s.key] || {};
        row[s.key] = {
          volume: Object.values(v).reduce((a, b) => a + b, 0),
          count: Object.values(c).reduce((a, b) => a + b, 0),
        };
      }
      return row;
    });
  }

  get breakdownFields() {
    return [
      { key: 'period', label: this.$t('accounting.revenue-funnel.period') },
      {
        key: 'label',
        label:
          this.breakdown === 'team'
            ? this.$t('accounting.revenue-funnel.team')
            : this.breakdown === 'account'
            ? this.$t('accounting.revenue-funnel.commercial-account')
            : this.$t('accounting.revenue-funnel.source'),
      },
      ...this.stages.map((s) => ({
        key: s.key,
        label: this.stageLabel(s.key),
        class: 'text-right',
      })),
    ];
  }

  // One block per period: its groups largest-first by created volume, then a
  // subtotal row. Every group with any activity in the period is listed --
  // the filters above are the way to narrow it, not a hidden cut-off.
  get breakdownRows() {
    const blocks = new Map<string, Map<string, BreakdownRow>>();
    for (const key of this.periodKeys) blocks.set(key, new Map());
    for (const r of this.filteredRows) {
      const block = blocks.get(r.period);
      if (!block) continue;
      const key = this.segmentOf(r);
      let g = block.get(key);
      if (!g) {
        g = {
          period: this.periodLabel(r.period),
          label: this.segmentLabel(key),
          sources: [],
        };
        for (const s of this.stages) g[s.key] = 0;
        block.set(key, g);
      }
      g[r.stage] = (g[r.stage] || 0) + r.volume;
      if (!g.sources.includes(r.source)) g.sources.push(r.source);
    }
    const rows: BreakdownRow[] = [];
    for (const [period, block] of blocks) {
      if (!block.size) continue;
      const groups = [...block.values()]
        .map((g): BreakdownRow => ({ ...g, sources: [...g.sources].sort() }))
        .sort((a, b) => (b.created || 0) - (a.created || 0));
      groups[0].first = true;
      rows.push(...groups);
      const subtotal: BreakdownRow = {
        period: this.periodLabel(period),
        label: this.$t('accounting.revenue-funnel.total') as string,
        subtotal: true,
        sources: [],
      };
      for (const s of this.stages) {
        subtotal[s.key] = groups.reduce((sum, g) => sum + (g[s.key] || 0), 0);
      }
      rows.push(subtotal);
    }
    return rows;
  }

  breakdownRowClass(item: BreakdownRow | null) {
    return item?.subtotal ? 'font-weight-bold table-light' : '';
  }

  // --- sync freshness -------------------------------------------------------

  get lastRunAt(): string | null {
    const runs = this.sync
      .map((s) => s.last_run_at)
      .filter(Boolean) as string[];
    return runs.length ? runs.sort()[0] : null;
  }

  get syncLabel(): string {
    if (!this.lastRunAt) return '';
    return this.$t('accounting.revenue-funnel.synced', {
      when: moment(this.lastRunAt).format('L LT'),
    }) as string;
  }

  get syncStale(): boolean {
    if (!this.showS4c || !this.sync.length) return false;
    if (this.sync.some((s) => !s.ok)) return true;
    return this.lastRunAt
      ? moment().diff(moment(this.lastRunAt), 'hours') > STALE_AFTER_HOURS
      : true;
  }

  get undatedInvoiced(): number {
    return this.undated.s4c_invoiced || 0;
  }

  // --- labels -----------------------------------------------------------------

  stageLabel(key: string): string {
    return this.$te(`accounting.revenue-funnel.stage.${key}`)
      ? (this.$t(`accounting.revenue-funnel.stage.${key}`) as string)
      : this.stages.find((s) => s.key === key)?.label || key;
  }

  sourceLabel(source: Source): string {
    return this.$t(`accounting.revenue-funnel.source-${source}`) as string;
  }

  teamLabel(key: string): string {
    return this.$te(`accounting.revenue-funnel.team-label.${key}`)
      ? (this.$t(`accounting.revenue-funnel.team-label.${key}`) as string)
      : key;
  }

  periodLabel(key: string): string {
    return moment(key).format(PERIOD_FORMAT[this.timeWindow]);
  }

  formatEur(value: number): string {
    return formatKEur(value);
  }

  formatPct(value: number): string {
    return pct.format(value);
  }

  // --- data -----------------------------------------------------------------

  async fetch() {
    const start = moment(this.dateStart);
    const end = moment(this.dateEnd);
    if (!start.isValid() || !end.isValid()) return;
    this.inProgress = true;
    this.errorMessage = '';
    try {
      const { data } = await Api.get('/api/admin/accounting/revenue-funnel', {
        params: {
          time_window: this.timeWindow,
          date_start: start.format('YYYY-MM-DD'),
          date_end: end.format('YYYY-MM-DD'),
        },
        autoCancelScope: 'revenue-funnel',
      });
      this.rows = data.rows || [];
      this.stages = data.stages || [];
      this.sync = data.sync || [];
      this.undated = data.undated || {};
      this.brandAliases = data.brand_aliases || [];
      this.fetchedStart = data.date_start;
      this.fetchedEnd = data.date_end;
      // drop selections that no longer exist in the new dataset
      const teams = new Set(this.rows.map((r) => r.team));
      this.selectedTeams = this.selectedTeams.filter((t) => teams.has(t.value));
      const accounts = new Set(this.rows.map((r) => r.commercial_account));
      this.selectedAccounts = this.selectedAccounts.filter((a) =>
        accounts.has(a.value),
      );
    } catch (e: any) {
      this.errorMessage = e?.response?.data?.message || e?.message || String(e);
      this.rows = [];
    } finally {
      this.inProgress = false;
    }
  }
}
